import { UNKNOWN_DATUM } from 'business/constants';
import { SortDimensionT } from 'business/definitions';
import {
    X_AXIS_DIMENSION,
    Y_AXIS_DIMENSION,
} from 'routes/Explore/components/Settings/components/ChartSettingsDrawer/helpers/sortDimensionSettings';
import {
    DatasetDimension,
    DEFAULT_DATASET_NAME,
    isTree,
} from './datasetHelpers';
import { isDictAllDataValueT } from 'helpers/eChartHelpers';

const getSortDimension = (
    dimension: string,
    yColumnKey: string,
    xColumnKey: string | undefined,
) => {
    switch (dimension) {
        case Y_AXIS_DIMENSION:
            return yColumnKey;
        case X_AXIS_DIMENSION:
            return xColumnKey ?? dimension;
        default:
            return dimension;
    }
};
/** Parse the transfom config for tree data.
 * @param {SortDimensionT} sortDimension - the raw sort dimension setting
 * @param {string[]} treeColumns - list of the request tree columns
 * @returns {SortDimensionT} the parsed sortDimension setting
 */
const getSortTransformConfig = (
    sortDimension: SortDimensionT,
    treeColumns: string[],
    yColumnKey: string,
    xColumnKey: string | undefined,
) => {
    const dimension = getSortDimension(
        sortDimension.dimension,
        yColumnKey,
        xColumnKey,
    );
    if (treeColumns.includes(dimension)) {
        return {
            ...sortDimension,
            dimension: `${dimension}.label`,
        };
    }
    return {
        ...sortDimension,
        dimension,
    };
};

const alphabeticalSort = (a: string, b: string) => (a > b ? 1 : -1);

/** Returns sorted x values based on the sortDimension and the data
 * If sortDimension is not defined, returns all unique x values
 * If sortDimension is defined, returns all unique x values sorted by the sortDimension
 * @param {ResponseDataT[]} data - The chart data
 * @param {ResponseColumnT | undefined} xColumn - Definition of the x column
 * @param {ResponseColumnT} yColumn - Definition of the y column
 * @param {SortDimensionT} sortDimension - The configuration of the sort dimension in the chart settings
 * @returns {ResponseDataValueT[]} - The sorted X axis values
 */
export const getSortedXValues = (
    data: ResponseDataT[],
    xColumn: ResponseColumnT | undefined,
    yColumn: ResponseColumnT,
    sortDimension: SortDimensionT | undefined,
): ResponseDataValueT[] => {
    if (!xColumn) {
        return [];
    }
    if (!sortDimension) {
        const uniqStringifiedXDataValues = Array.from(
            new Set(data.map((row) => JSON.stringify(row[xColumn.key]))),
        );
        return uniqStringifiedXDataValues.map((item) => JSON.parse(item));
    }

    const isSortedByY = sortDimension.dimension === Y_AXIS_DIMENSION;
    const xDataValues = new Set<ResponseDataValueT>();
    const uniqValues: PlainResponseDataValueT[] = [];
    const weightedXAxisValues = data.reduce((acc, dataRow) => {
        const xRawValue = dataRow[xColumn.key];
        if (!xRawValue) {
            return acc;
        }
        if (isDictAllDataValueT(xRawValue)) {
            // With objects, using a Set does not guarantee the unicity.
            // We need to add the following manual check to make sure xDataValues does not contain duplicates.
            if (!uniqValues.includes(xRawValue.value)) {
                uniqValues.push(xRawValue.value);
                xDataValues.add(xRawValue);
            }
        } else {
            // When xRawValue is not of type DictAllDataValueT,
            // using the set is sufficient to handle the duplicates removal.
            xDataValues.add(xRawValue);
        }
        const xValue = isTree(xRawValue) ? xRawValue.value : xRawValue;
        const yValue = dataRow[yColumn.key];
        const currentValue = acc.get(xValue) ?? 0;
        if (yValue && yValue !== UNKNOWN_DATUM) {
            acc.set(xValue, currentValue + (yValue as number)); // This is clearly a bad cast, but get data results are a mess
        }
        return acc;
    }, new Map<ResponseDataValueT, number>());

    const sortedXAxisData = Array.from(xDataValues).sort((a, b) => {
        const unestedA = isTree(a) ? a.value : a;
        const unestedB = isTree(b) ? b.value : b;

        if (isSortedByY) {
            const aValue = weightedXAxisValues.get(unestedA) ?? 0;
            const bValue = weightedXAxisValues.get(unestedB) ?? 0;
            return sortDimension?.order === 'asc'
                ? aValue - bValue
                : bValue - aValue;
        }

        const aString = unestedA ?? '';
        const bString = unestedB ?? '';
        return sortDimension?.order === 'asc'
            ? alphabeticalSort(aString.toString(), bString.toString())
            : -alphabeticalSort(aString.toString(), bString.toString());
    });
    return sortedXAxisData;
};

/** Returns sorted dataset based on the sortDimension and the data
 * @param {ResponseDataT[]} mData - The chart data
 * @param {DatasetDimension[]} datasetDimensions - The dataset dimensions
 * @param {SortDimensionT} sortDimension - The configuration of the sort dimension in the chart settings
 * @param {string[]} treeColumns - list of the request tree columns
 * @returns {DatasetT[]} - The sorted dataset
 */
export const getSortedDataset = (
    mData: ResponseDataT[],
    datasetDimensions: DatasetDimension[],
    sortDimension: SortDimensionT,
    treeColumns: string[],
    yColumn: ResponseColumnT,
    xColumn: ResponseColumnT | undefined,
) => {
    const sortTransfomConfig = getSortTransformConfig(
        sortDimension,
        treeColumns,
        yColumn.key,
        xColumn?.key,
    );
    return [
        {
            id: `unsorted-${DEFAULT_DATASET_NAME}`,
            source: mData,
            dimensions: datasetDimensions,
        },
        {
            id: DEFAULT_DATASET_NAME,
            fromDatasetId: `unsorted-${DEFAULT_DATASET_NAME}`,
            transform: {
                type: 'sort',
                config: sortTransfomConfig,
            },
            dimensions: datasetDimensions,
        },
    ];
};
